<script setup lang="ts">
import { ref, computed, reactive, watch } from "vue";
import { close_small } from "pn-design-assets/pn-assets/icons";
import type {
  ApiCategoryTag,
  ApiTagCategory,
  CreateCategoryParams,
  UpdateCategoryParams,
} from "@/types/tagManagement";
import { useTagManagementStore } from "@/stores/tagManagement";

const tagManagementStore = useTagManagementStore();

const createDefaultParams = (): CreateCategoryParams => ({
  themeId: 0,
  parentCategoryId: null,
  swedishName: "",
  englishName: "",
  englishTags: [],
  swedishTags: [],
  isStickerCollection: true,
  isStickerIndividual: true,
});

const categoryParams = reactive<CreateCategoryParams>(createDefaultParams());
const isSaveButtonClicked = ref(false);
const tagValidationError = ref("");
const isSaving = ref(false);

const props = defineProps<{
  open?: boolean;
  parentCategoryId?: number | null;
  themeId?: number;
  title?: string;
  category?: ApiTagCategory;
  parentTags?: ApiCategoryTag[];
}>();

const open = computed(() => props.open ?? false);
const modalTitle = computed(() => props.title ?? "Add category");
const isEditMode = computed(() => !!props.category);
const parentSwedishTags = computed(
  () => props.parentTags?.find((t) => t.language === "swedish")?.tags ?? [],
);
const parentEnglishTags = computed(
  () => props.parentTags?.find((t) => t.language === "english")?.tags ?? [],
);

const reset = () => {
  Object.assign(categoryParams, createDefaultParams());
  isSaveButtonClicked.value = false;
  tagValidationError.value = "";
  isSaving.value = false;
};

const emit = defineEmits<{
  (e: "onClose"): void;
  (e: "onSave"): void;
}>();

watch(open, (now, prev) => {
  if (now === true && prev !== true) {
    if (props.category) {
      categoryParams.swedishName = props.category.svCategoryName;
      categoryParams.englishName = props.category.enCategoryName;
      categoryParams.swedishTags = [
        ...(props.category.tags.find((t) => t.language === "swedish")?.tags ??
          []),
      ];
      categoryParams.englishTags = [
        ...(props.category.tags.find((t) => t.language === "english")?.tags ??
          []),
      ];
      categoryParams.isStickerCollection =
        props.category.visibleFlags?.isStickerCollection ?? true;
      categoryParams.isStickerIndividual =
        props.category.visibleFlags?.isStickerIndividual ?? true;
    }
  }
  if (now === false && prev === true) {
    reset();
  }
});

const onClose = () => {
  reset();
  emit("onClose");
};

const validate = () => {
  tagValidationError.value = "";
  if (
    categoryParams.swedishName.trim() === "" ||
    categoryParams.englishName.trim() === ""
  ) {
    return false;
  }
  /* if (
    !isEditMode.value &&
    (categoryParams.englishTags.length === 0 ||
      categoryParams.swedishTags.length === 0)
  ) {
    tagValidationError.value =
      "Please add at least one Swedish tag and one English tag";
    return false;
  } */
  return true;
};

const onSave = async () => {
  isSaveButtonClicked.value = true;
  if (!validate()) {
    return;
  }
  isSaving.value = true;
  if (isEditMode.value && props.category) {
    const updatePayload: UpdateCategoryParams = {
      categoryId: props.category.categoryId,
      swedishName: categoryParams.swedishName,
      englishName: categoryParams.englishName,
      englishTags: [...categoryParams.englishTags],
      swedishTags: [...categoryParams.swedishTags],
      isStickerCollection: categoryParams.isStickerCollection,
      isStickerIndividual: categoryParams.isStickerIndividual,
    };
    await tagManagementStore.updateCategory(updatePayload);
  } else {
    const createPayload: CreateCategoryParams = {
      themeId: props.themeId ?? 0,
      parentCategoryId: props.parentCategoryId ?? null,
      swedishName: categoryParams.swedishName,
      englishName: categoryParams.englishName,
      englishTags: [...categoryParams.englishTags],
      swedishTags: [...categoryParams.swedishTags],
      isStickerCollection: categoryParams.isStickerCollection,
      isStickerIndividual: categoryParams.isStickerIndividual,
    };
    await tagManagementStore.createCategory(createPayload);
  }
  isSaving.value = false;
  emit("onSave");
};

const getNameError = (language: "swedish" | "english") => {
  if (!isSaveButtonClicked.value) {
    return undefined;
  }
  if (language === "swedish" && categoryParams.swedishName.trim() === "") {
    return "Swedish name is required";
  }
  if (language === "english" && categoryParams.englishName.trim() === "") {
    return "English name is required";
  }
  return undefined;
};

const onTagEntered = (event: Event, language: "swedish" | "english") => {
  const inputEl = event.target as HTMLInputElement;
  const tag = inputEl.value.trim();
  if (tag === "") {
    return;
  }
  if (language === "swedish") {
    categoryParams.swedishTags.push(tag);
  } else {
    categoryParams.englishTags.push(tag);
  }
  inputEl.value = "";
};

const removeTag = (tag: string, language: "swedish" | "english") => {
  if (language === "swedish") {
    const tagIndex = categoryParams.swedishTags.indexOf(tag);
    if (tagIndex > -1) {
      categoryParams.swedishTags.splice(tagIndex, 1);
    }
  } else {
    const tagIndex = categoryParams.englishTags.indexOf(tag);
    if (tagIndex > -1) {
      categoryParams.englishTags.splice(tagIndex, 1);
    }
  }
};

const onStickerCollectionToggle = (event: Event) => {
  const target = event.target as HTMLInputElement;
  categoryParams.isStickerCollection = target.checked;
};

const onStickerIndividualToggle = (event: Event) => {
  const target = event.target as HTMLInputElement;
  categoryParams.isStickerIndividual = target.checked;
};
</script>

<template>
  <pn-modal :open="open" class="add-category-modal" hide-close @close="onClose">
    <div :class="[{ spinner: isSaving }]">
      <pn-spinner size="3" v-show="isSaving" />
    </div>
    <h2 class="pn-l">{{ modalTitle }}</h2>
    <section class="flex flex-direction-column gap-24">
      <div class="flex justify-space-between gap-32">
        <div class="flex flex-direction-column add-category-col">
          <pn-input
            label="Swedish Name"
            :value="categoryParams.swedishName"
            :error="getNameError('swedish')"
            @input="
              categoryParams.swedishName = (
                $event.target as HTMLInputElement
              ).value
            "
          ></pn-input>
          <p class="pn-xs display-name-label">
            <strong>Display name:</strong>
            {{ categoryParams.swedishName || "—" }}
          </p>
        </div>
        <div class="flex flex-direction-column add-category-col">
          <pn-input
            label="English Name"
            :value="categoryParams.englishName"
            :error="getNameError('english')"
            @input="
              categoryParams.englishName = (
                $event.target as HTMLInputElement
              ).value
            "
          ></pn-input>
        </div>
      </div>
      <div class="flex justify-space-between gap-32">
        <div class="flex flex-direction-column add-category-col">
          <pn-input
            label="Swedish Tags"
            helpertext="Enter one tag at a time and press the SPACE BAR."
            @keydown.space.prevent="onTagEntered($event, 'swedish')"
          ></pn-input>
          <div class="flex gap-4 flex-wrap">
            <pn-tag
              small
              appearance="discrete"
              :label="tag"
              v-for="(tag, tagIndex) in parentSwedishTags"
              :key="`sv-parent-tag-${tagIndex}`"
            ></pn-tag>
            <pn-tag
              small
              :label="tag"
              :icon="close_small"
              v-for="(tag, tagIndex) in categoryParams.swedishTags"
              :key="`sv-tag-${tagIndex}`"
              @click="removeTag(tag, 'swedish')"
            ></pn-tag>
          </div>
          <pn-toggle-switch
            label="Sticker Collection"
            :checked="categoryParams.isStickerCollection"
            @change="onStickerCollectionToggle"
          ></pn-toggle-switch>
        </div>
        <div class="flex flex-direction-column add-category-col">
          <pn-input
            label="English Tags"
            helpertext="Enter one tag at a time and press the SPACE BAR."
            @keydown.space.prevent="onTagEntered($event, 'english')"
          ></pn-input>
          <div class="flex gap-4 flex-wrap">
            <pn-tag
              small
              appearance="discrete"
              :label="tag"
              v-for="(tag, tagIndex) in parentEnglishTags"
              :key="`en-parent-tag-${tagIndex}`"
            ></pn-tag>
            <pn-tag
              small
              :label="tag"
              :icon="close_small"
              v-for="(tag, tagIndex) in categoryParams.englishTags"
              :key="`en-tag-${tagIndex}`"
              @click="removeTag(tag, 'english')"
            ></pn-tag>
          </div>
          <pn-toggle-switch
            label="Sticker Individual"
            :checked="categoryParams.isStickerIndividual"
            @change="onStickerIndividualToggle"
          ></pn-toggle-switch>
        </div>
      </div>
      <p v-if="tagValidationError" class="pn-xs tag-validation-error">
        {{ tagValidationError }}
      </p>
    </section>
    <!-- eslint-disable vue/no-deprecated-slot-attribute -->
    <div slot="buttons">
      <pn-button appearance="light" variant="outlined" @click="onClose"
        >Cancel</pn-button
      >
      <pn-button @click="onSave">Save</pn-button>
    </div>
  </pn-modal>
</template>

<style>
.add-category-modal .pn-modal {
  max-width: 80% !important;
  width: 80% !important;
}
.add-category-col {
  width: 50%;
  min-width: 0;
}
.add-category-col pn-input {
  width: 100%;
}
.display-name-label {
  margin-top: 4px;
  color: var(--pn-color-text-secondary, #555);
}
.tag-validation-error {
  color: var(--pn-color-error, #d32f2f);
}
</style>
